import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { SITE_COPY_CONTENT_KEY } from "@tih/shared";
import { ApiError, adminFetchContent, adminPutContent } from "../../api/client";

function contentKeyFromSearch(raw: string | null): string {
  if (raw && /^[a-z0-9_]+$/.test(raw) && raw.length <= 64) return raw;
  return "event";
}

/** Legacy JSON editor for SiteContent keys (event fallback, site_copy bulk edits). */
export function AdminContentJsonPage() {
  const [searchParams] = useSearchParams();
  const key = contentKeyFromSearch(searchParams.get("key"));

  const [text, setText] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminFetchContent(key)
      .then((r) => {
        setText(JSON.stringify(r.valueJson, null, 2));
        setUpdatedAt(r.updatedAt);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) {
          setText("{}");
        } else {
          setError(String((e as Error).message ?? e));
        }
      })
      .finally(() => setLoading(false));
  }, [key]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setError("Invalid JSON");
      setBusy(false);
      return;
    }
    try {
      const saved = await adminPutContent(key, parsed);
      setUpdatedAt(saved.updatedAt);
      setSuccess(
        key === SITE_COPY_CONTENT_KEY
          ? "Saved. Public /api/copy will merge these overrides with bundled defaults."
          : key === "event"
            ? "Saved. Public /api/event will reflect the new content when no future scheduled event exists."
            : "Saved.",
      );
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not save content");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="admin-page" style={{ opacity: 0.5 }}>Loading…</div>;

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Content JSON</h1>
        <p>
          Advanced editor for key <code>{key}</code>.{" "}
          {key === "event" ? (
            <>
              JSON fallback for <code>/api/event</code> when there is no future row in{" "}
              <a href="/admin/events">Events</a>.
            </>
          ) : key === SITE_COPY_CONTENT_KEY ? (
            <>
              Partial JSON merged over marketing defaults. Prefer{" "}
              <a href="/admin/copy">Marketing copy</a> for a guided form.
            </>
          ) : (
            <>Arbitrary JSON stored at this key.</>
          )}
          {updatedAt && <> Last updated {new Date(updatedAt).toLocaleString()}.</>}
        </p>
      </header>

      <form onSubmit={onSubmit} className="admin-content-form">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy}
          spellCheck={false}
          className="admin-content-editor"
          rows={32}
        />
        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success" style={{ padding: 12 }}>{success}</div>}
        <div className="admin-blog-form-actions">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Saving…" : "Save content"}
          </button>
        </div>
      </form>
    </div>
  );
}
